import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Logout from '../components/LogOut'; 
import logo from "../asset/logo.webp"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faUser, faSignInAlt, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';

/**
 * @module Components/Header
 * @description Header est un composant qui affiche le logo et la barre de navigation en haut de l'application.
 * - Il contient des liens de navigation vers différentes pages de l'application.
 * - Il utilise le composant NavLink de 'react-router-dom' pour créer ces liens.
 * - Il utilise le hook useSelector de 'react-redux' pour accéder à l'état de l'utilisateur dans le store Redux.
 * 
 */

const Header = () => {
    const isLoggedIn = useSelector(state => state.user.isLoggedIn);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    return (
        <header className="bg-white shadow-lg border-b border-gray-200 relative">
            <div className="container-responsive">
                <div className="flex items-center justify-between py-3 md:py-4">
                    {/* Logo */}
                    <NavLink to="/" className="flex items-center" onClick={closeMobileMenu}>
                        <img 
                            src={logo} 
                            className="h-12 md:h-16 w-auto object-contain" 
                            alt="Ça, c'est pour Moi !" 
                        />
                    </NavLink>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
                        {isLoggedIn ? (
                            <>
                                <NavLink 
                                    to="/" 
                                    className={({ isActive }) => 
                                        `px-3 lg:px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                                            isActive 
                                                ? "bg-primary text-white shadow-md" 
                                                : "text-primary hover:bg-primary hover:text-white hover:shadow-md"
                                        }`
                                    }
                                >
                                    <FontAwesomeIcon icon={faHome} className="mr-2" />
                                    Accueil
                                </NavLink>
                                <NavLink 
                                    to="/Profil" 
                                    className={({ isActive }) => 
                                        `px-3 lg:px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                                            isActive 
                                                ? "bg-primary text-white shadow-md" 
                                                : "text-primary hover:bg-primary hover:text-white hover:shadow-md"
                                        }`
                                    }
                                >
                                    <FontAwesomeIcon icon={faUser} className="mr-2" />
                                    Mon profil
                                </NavLink>
                                <Logout />
                            </>
                        ) : (
                            <NavLink 
                                to="/Signin" 
                                className={({ isActive }) => 
                                    `px-4 lg:px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                                        isActive 
                                            ? "bg-primary text-white shadow-md" 
                                            : "bg-primary text-white hover:bg-primary-light shadow-md hover:shadow-lg"
                                    }`
                                }
                            >
                                <FontAwesomeIcon icon={faSignInAlt} className="mr-2" />
                                Se connecter
                            </NavLink>
                        )}
                    </nav>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={toggleMobileMenu}
                        className="md:hidden p-2 rounded-lg text-primary hover:bg-gray-100 transition-colors duration-200"
                        aria-label="Menu"
                    >
                        <FontAwesomeIcon 
                            icon={isMobileMenuOpen ? faTimes : faBars} 
                            className="text-xl" 
                        />
                    </button>
                </div>

                {/* Mobile Navigation Menu */}
                {isMobileMenuOpen && (
                    <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-b border-gray-200 z-50">
                        <nav className="px-4 py-4 space-y-2">
                            {isLoggedIn ? (
                                <>
                                    <NavLink 
                                        to="/" 
                                        onClick={closeMobileMenu}
                                        className={({ isActive }) => 
                                            `flex items-center px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                                                isActive 
                                                    ? "bg-primary text-white shadow-md" 
                                                    : "text-primary hover:bg-primary hover:text-white hover:shadow-md"
                                            }`
                                        }
                                    >
                                        <FontAwesomeIcon icon={faHome} className="mr-3" />
                                        Accueil
                                    </NavLink>
                                    <NavLink 
                                        to="/Profil" 
                                        onClick={closeMobileMenu}
                                        className={({ isActive }) => 
                                            `flex items-center px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                                                isActive 
                                                    ? "bg-primary text-white shadow-md" 
                                                    : "text-primary hover:bg-primary hover:text-white hover:shadow-md"
                                            }`
                                        }
                                    >
                                        <FontAwesomeIcon icon={faUser} className="mr-3" />
                                        Mon profil
                                    </NavLink>
                                    <div className="px-4 py-3">
                                        <Logout />
                                    </div>
                                </>
                            ) : (
                                <NavLink 
                                    to="/Signin" 
                                    onClick={closeMobileMenu}
                                    className={({ isActive }) => 
                                        `flex items-center px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                                            isActive 
                                                ? "bg-primary text-white shadow-md" 
                                                : "bg-primary text-white hover:bg-primary-light shadow-md hover:shadow-lg"
                                        }`
                                    }
                                >
                                    <FontAwesomeIcon icon={faSignInAlt} className="mr-3" />
                                    Se connecter
                                </NavLink>
                            )}
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
};

export default Header;